// src/components/authentication/ForgotPasswordRequest.js
/* ------------------------------------------------------------------ */
/* ForgotPasswordRequest – send reset-link e-mail                      */
/* ------------------------------------------------------------------ */
/**
 * • Collects user’s e-mail → platform password-reset endpoint (user-service via
 *   the regional gateway). The backend generates the Firebase Auth reset link
 *   and sends the branded, localised email — the same mechanism the patient web
 *   app uses.
 * • Shows confirmation that a link was sent (even if the e-mail is unknown,
 *   to avoid account enumeration).
 */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { EMAIL_REGEX } from "../../utils/constants";
import { requestPasswordReset } from "../../api/auth";
import usePopup from "../../hooks/usePopup";
import ErrorPopup from "../ErrorPopup";
import BrandLogo from "../branding/BrandLogo";
import { useTranslation } from "react-i18next";

/* Component */
export default function ForgotPasswordRequest() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { popup, open: openPopup, close: closePopup } = usePopup();

  const handleSubmit = async (e) => {
    e.preventDefault();
    closePopup();

    const normalisedEmail = email.trim();

    if (!EMAIL_REGEX.test(normalisedEmail)) {
      openPopup({ message: t("forgotPassword.emailRegexError") });
      return;
    }

    try {
      await requestPasswordReset({
        email: normalisedEmail,
        origin: window.location.origin,
        // Send the reset email in the user's currently selected language.
        language: i18n.resolvedLanguage || i18n.language,
      });
      setEmail(normalisedEmail);
      setSent(true);
    } catch (err) {
      openPopup({ message: t("forgotPassword.emailSendError") });
    }
  };

  return (
    <div className="w-full max-w-lg h-full p-4 flex flex-col justify-end md:justify-start items-center gap-4">
      {popup && <ErrorPopup popup={popup} onClose={closePopup} />}

      <BrandLogo alt="pDx logo" className="h-24" />
      <h1 className="title-text">{t("forgotPassword.title")}</h1>

      {sent ? (
        <>
          <p className="text-center">
            {t("forgotPassword.exists1")}
            <strong>{email}</strong> {t("forgotPassword.exists2")}
          </p>
          <p className="text-center text-sm opacity-75">
            {t("forgotPassword.spamNote")}
          </p>
          <Link to="/auth/login" className="link">
            {t("forgotPassword.back")}
          </Link>
        </>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full">
          <input
            type="email"
            placeholder={t("forgotPassword.email")}
            className="custom-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit" className="primary-button">
            {t("forgotPassword.cta")}
          </button>
        </form>
      )}
    </div>
  );
}
